import { useState, useEffect, useCallback } from 'react'
import { NavBar, Search, List, Cell, Tag, Empty, DropdownMenu, Loading, Tabs } from 'react-vant'
import { ArrowLeft } from '@react-vant/icons'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import request from '../../api/request'

export default function Query() {
  const navigate = useNavigate()
  const { userInfo } = useSelector(state => state.user)

  const [activeTab, setActiveTab] = useState('physical')
  const [academicYear, setAcademicYear] = useState('')
  const [classId, setClassId] = useState('')
  const [searchValue, setSearchValue] = useState('')

  const [loading, setLoading] = useState(false)
  const [yearOptions, setYearOptions] = useState([])
  const [classList, setClassList] = useState([{ text: '全部班级', value: '' }])
  const [results, setResults] = useState([])
  const [academicResults, setAcademicResults] = useState([])
  const [myStudentId, setMyStudentId] = useState(null) // 学生/家长角色：绑定的学生ID

  const isTeacher = userInfo?.role === 'teacher' || userInfo?.role === 'admin'

  // 0. 学生/家长角色：获取绑定的学生ID
  useEffect(() => {
    if (isTeacher) return
    const fetchMyStudent = async () => {
      try {
        const res = await request.get('/academic/students/m/children/')
        if (res.success && res.data && res.data.length > 0) {
          setMyStudentId(res.data[0].id)
        }
      } catch (e) {
        console.error('获取绑定学生失败:', e)
      }
    }
    fetchMyStudent()
  }, [isTeacher])

  // 1. 加载学年选项
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const res = await request.get('/academic-year/options/')
        if (res.success && res.options) {
          const options = res.options.map(o => ({ text: String(o.label), value: String(o.value) }))
          setYearOptions(options)
          if (res.current_year && !academicYear) {
            setAcademicYear(String(res.current_year))
          }
        }
      } catch (e) {
        console.error('Fetch years failed', e)
      }
    }
    fetchYears()
  }, [])

  // 2. 联动加载班级
  useEffect(() => {
    if (!academicYear) return
    const fetchClasses = async () => {
      try {
        const res = await request.get('/academic/classes/my/', {
          params: { academic_year: academicYear, page_size: 100 }
        })
        const options = [
          { text: '全部班级', value: '' },
          ...(res.items || []).map(c => ({ text: c.grade ? `${c.grade} ${c.class_name}` : String(c.class_name), value: String(c.id) }))
        ]
        setClassList(options)
      } catch (e) {
        console.error('Fetch classes failed', e)
      }
    }
    fetchClasses()
  }, [academicYear])

  // 3. 获取体测成绩
  const fetchPhysicalResults = async () => {
    const params = {
      academic_year: academicYear || undefined,
      class_id: classId || undefined,
      search: searchValue || undefined,
      page_size: 50
    }
    // 学生/家长角色：必须限制为自己的 student_id
    if (!isTeacher && myStudentId) {
      params.student_id = myStudentId
    }
    setLoading(true)
    try {
      const res = await request.get('/sports/tests/', { params })
      setResults(res.items || [])
    } catch (e) {
      console.error('Fetch results failed', e)
    } finally {
      setLoading(false)
    }
  }

  // 4. 获取文化课成绩
  const fetchAcademicResults = async () => {
    const params = {
      academic_year: academicYear || undefined,
      class_id: classId || undefined,
      search: searchValue || undefined,
      page_size: 100
    }
    // 学生/家长角色：必须限制为自己的 student_id
    if (!isTeacher && myStudentId) {
      params.student_id = myStudentId
    }
    setLoading(true)
    try {
      const res = await request.get('/academic/scores/', { params })
      setAcademicResults(res.items || [])
    } catch (e) {
      console.error('Fetch academic results failed', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // 学生/家长角色必须等 myStudentId 就绪再加载
    if (!academicYear) return
    if (!isTeacher && !myStudentId) return
    if (activeTab === 'physical') {
      fetchPhysicalResults()
    } else {
      fetchAcademicResults()
    }
  }, [academicYear, classId, activeTab, searchValue, myStudentId])


  const onSearch = () => {
    if (activeTab === 'physical') {
      fetchPhysicalResults()
    } else {
      fetchAcademicResults()
    }
  }

  const getScoreColor = (score) => {
    if (score >= 90) return '#00b578' // 优秀 - 深绿
    if (score >= 80) return '#056b4f' // 良好 - 浅绿
    if (score >= 60) return '#fa8c16' // 及格
    return '#e11d48' // 不及格
  }

  const currentYearText = yearOptions.find(o => o.value === academicYear)?.text || '选择学年'
  const currentClassText = classList.find(o => o.value === classId)?.text || '全部班级'

  return (
    <div className="query-page" style={{ background: 'var(--canvas-bg, #f8fafc)', minHeight: '100vh' }}>
      <NavBar title="综合成绩查询" leftArrow={<ArrowLeft />} onClickLeft={() => navigate(-1)} />

      <Tabs active={activeTab} onChange={setActiveTab} sticky offsetTop={0} color="var(--primary-color, #056b4f)">
        <Tabs.TabPane name="physical" title="体测成绩" />
        <Tabs.TabPane name="academic" title="文化课成绩" />
      </Tabs>

      {isTeacher && (
        <div style={{ background: '#fff' }}>
          <Search
            value={searchValue}
            onChange={setSearchValue}
            onSearch={onSearch}
            placeholder="搜索姓名 / 教育ID"
          />

          <div style={{ borderBottom: '1px solid #f1f5f9' }}>
            <DropdownMenu key={`${academicYear}-${classId}`} activeColor="var(--primary-color, #056b4f)">
              <DropdownMenu.Item title={currentYearText} value={academicYear}>
                <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                  {yearOptions.map(opt => (
                    <Cell
                      key={opt.value}
                      title={opt.text}
                      onClick={() => setAcademicYear(opt.value)}
                      style={{ color: academicYear === String(opt.value) ? 'var(--primary-color, #056b4f)' : 'inherit' }}
                    />
                  ))}
                </div>
              </DropdownMenu.Item>
              <DropdownMenu.Item title={currentClassText} value={classId}>
                <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                  {classList.map(opt => (
                    <Cell
                      key={opt.value}
                      title={opt.text}
                      onClick={() => setClassId(opt.value)}
                      style={{ color: classId === String(opt.value) ? 'var(--primary-color, #056b4f)' : 'inherit' }}
                    />
                  ))}
                </div>
              </DropdownMenu.Item>
            </DropdownMenu>
          </div>
        </div>
      )}

      {!isTeacher && (
        <div style={{ borderBottom: '1px solid #f1f5f9', background: '#fff' }}>
          <DropdownMenu key={academicYear} activeColor="var(--primary-color, #056b4f)">
            <DropdownMenu.Item title={`查询学年: ${currentYearText}`} value={academicYear}>
              <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                {yearOptions.map(opt => (
                  <Cell
                    key={opt.value}
                    title={opt.text}
                    onClick={() => setAcademicYear(opt.value)}
                    style={{ color: academicYear === String(opt.value) ? 'var(--primary-color, #056b4f)' : 'inherit' }}
                  />
                ))}
              </div>
            </DropdownMenu.Item>
          </DropdownMenu>
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Loading vertical>加载中...</Loading></div>
        ) : (
          <>
            {activeTab === 'physical' ? (
              results.length > 0 ? (
                (() => {
                  const uniqueNames = [...new Set(results.map(r => r.student_name))]
                  const isSingle = uniqueNames.length === 1

                  return (
                    <List finished={true}>
                      {results.map(item => (
                        <Cell
                          key={item.id}
                          title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {!isSingle && <span style={{ fontWeight: 500 }}>{item.student_name}</span>}
                              <span style={{
                                fontSize: 13,
                                padding: '1px 8px',
                                background: item.gender === 'male' ? '#f1f5f9' : '#fff1f2',
                                color: item.gender === 'male' ? '#475569' : '#be123c',
                                borderRadius: 4,
                                fontWeight: 600,
                                letterSpacing: '0.5px'
                              }}>
                                {item.gender === 'male' ? '男' : '女'}
                              </span>
                            </div>
                          }
                          label={
                            <div style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 4 }}>
                              {item.class_name} {item.education_id ? `| ${item.education_id} ` : ''}| {item.test_date}
                            </div>
                          }
                          value={
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: 18, fontWeight: 'bold', color: getScoreColor(item.total_score) }}>
                                {item.total_score}
                              </div>
                              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                {{
                                  'A': '优秀', 'B': '良好', 'C': '及格', 'D': '不及格',
                                  '优秀': '优秀', '良好': '良好', '及格': '及格', '不及格': '不及格'
                                }[item.grade_name] || item.grade_name}
                              </div>
                            </div>
                          }
                          isLink
                          onClick={() => navigate(`../detail/${item.id}`)}
                        />
                      ))}
                    </List>
                  )
                })()
              ) : (
                <Empty description="未找到体测数据" />
              )
            ) : (
              academicResults.length > 0 ? (
                (() => {
                  const uniqueNames = [...new Set(academicResults.map(r => r.student_name))]
                  const isSingle = uniqueNames.length === 1

                  return (
                    <List finished={true}>
                      {academicResults.map(item => {
                        const examTypeMap = {
                          'midterm': '期中考试',
                          'final': '期末考试',
                          'regular': '平时成绩'
                        }
                        const examTypeText = examTypeMap[item.exam_type] || item.exam_type || '常规考试'

                        // 定义学科主题色
                        const getSubjectTheme = (name) => {
                          const themes = {
                            '语文': { bg: 'linear-gradient(135deg, #04523c 0%, #0c8c68 100%)', icon: '语' },
                            '数学': { bg: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)', icon: '数' },
                            '英语': { bg: 'linear-gradient(135deg, #d97706 0%, #ff804a 100%)', icon: '英' },
                            '体育': { bg: 'linear-gradient(135deg, #00b578 0%, #34d399 100%)', icon: '体' },
                          }
                          return themes[name] || { bg: 'linear-gradient(135deg, #94a3b8 0%, #94a3b8 100%)', icon: name?.[0] || '科' }
                        }
                        const theme = getSubjectTheme(item.subject_name)

                        return (
                          <Cell
                            key={item.id}
                            style={{ padding: '16px 16px' }}
                            title={
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                  width: 42,
                                  height: 42,
                                  borderRadius: 10,
                                  background: theme.bg,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#fff',
                                  fontSize: 18,
                                  fontWeight: 'bold',
                                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                }}>
                                  {theme.icon}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  {!isSingle && <span style={{ fontSize: 13, color: '#94a3b8', marginBottom: 2 }}>{item.student_name}</span>}
                                  <span style={{ fontSize: 16, fontWeight: 600, color: '#0f172a' }}>{item.subject_name}</span>
                                </div>
                              </div>
                            }
                            label={
                              <div style={{ marginTop: 4, fontSize: 13, color: '#94a3b8' }}>
                                {item.class_name} | {examTypeText}
                              </div>
                            }
                            value={
                              <div style={{ textAlign: 'right' }}>
                                <div style={{
                                  fontSize: 24,
                                  fontFamily: 'DIN Alternate, system-ui',
                                  fontWeight: 'bold',
                                  color: getScoreColor(item.score),
                                  lineHeight: 1
                                }}>
                                  {item.score}
                                </div>
                                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                                  {{
                                    'A': '优秀', 'B': '良好', 'C': '及格', 'D': '不及格',
                                    '优秀': '优秀', '良好': '良好', '及格': '及格', '不及格': '不及格'
                                  }[item.grade] || item.grade || ''}
                                </div>
                              </div>
                            }
                          />
                        )
                      })}
                    </List>
                  )
                })()
              ) : (
                <Empty description="未找到文化课成绩" />
              )
            )}
          </>
        )}
      </div>
    </div>
  )
}
